<template>
  <div class="tabs">
    <div
        class="tabs__list"
        role="tablist"
        :aria-label="ariaLabel">
      <button
          v-for="(tab, index) in tabs"
          :id="getTabId(tab.id)"
          :key="tab.id"
          :ref="(element) => setTabButtonRef(element, index)"
          class="button button--large"
          :class="{ 'button--inverted': activeTabId !== tab.id }"
          role="tab"
          type="button"
          :aria-selected="activeTabId === tab.id"
          :aria-controls="getPanelId(tab.id)"
          :tabindex="activeTabId === tab.id ? 0 : -1"
          :disabled="tab.disabled"
          @click="setActiveTab(tab.id)"
          @keydown="onTabKeydown($event, index)">
        {{ tab.label }}
      </button>
    </div>

    <div
        v-for="tab in tabs"
        v-show="activeTabId === tab.id"
        :id="getPanelId(tab.id)"
        :key="tab.id"
        class="tabs__panel"
        role="tabpanel"
        :aria-labelledby="getTabId(tab.id)">
      <slot :name="tab.id" :tab="tab"/>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId, watch } from "vue";
import type { ComponentPublicInstance } from "vue";

const props = defineProps<{
  tabs: TabsItem[];
  modelValue?: string;
  ariaLabel?: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const generatedId = useId();
const tabButtonElements = ref<(HTMLButtonElement | null)[]>([]);
const keyArrowLeft = "ArrowLeft";
const keyArrowRight = "ArrowRight";
const keyHome = "Home";
const keyEnd = "End";
const navigationKeys = [keyArrowLeft, keyArrowRight, keyHome, keyEnd];

const firstEnabledTabId = computed(() => props.tabs.find((tab) => !tab.disabled)?.id ?? "");
const activeTabId = ref(getInitialTabId());

watch(
    () => props.modelValue,
    (newValue) => {
      if (isSelectableTab(newValue)) {
        activeTabId.value = newValue;
        return;
      }

      ensureValidActiveTab();
    },
);

watch(
    () => props.tabs,
    () => {
      tabButtonElements.value = [];
      ensureValidActiveTab();
    },
    {deep: true},
);

function getInitialTabId(): string {
  if (isSelectableTab(props.modelValue)) {
    return props.modelValue;
  }

  return firstEnabledTabId.value;
}

function isSelectableTab(tabId?: string): tabId is string {
  return Boolean(tabId) && props.tabs.some((tab) => tab.id === tabId && !tab.disabled);
}

function ensureValidActiveTab(): void {
  if (isSelectableTab(activeTabId.value)) {
    return;
  }

  const nextTabId = firstEnabledTabId.value;
  activeTabId.value = nextTabId;

  if (nextTabId && props.modelValue !== nextTabId) {
    emit("update:modelValue", nextTabId);
  }
}

function setActiveTab(tabId: string): void {
  if (!isSelectableTab(tabId) || activeTabId.value === tabId) {
    return;
  }

  activeTabId.value = tabId;
  emit("update:modelValue", tabId);
}

function setTabButtonRef(element: Element | ComponentPublicInstance | null, index: number): void {
  tabButtonElements.value[index] = element instanceof HTMLButtonElement ? element : null;
}

function onTabKeydown(event: KeyboardEvent, currentIndex: number): void {
  if (!navigationKeys.includes(event.key)) {
    return;
  }

  event.preventDefault();

  const enabledIndexes = props.tabs
      .map((tab, index) => ({index, disabled: tab.disabled === true}))
      .filter((item) => !item.disabled)
      .map((item) => item.index);

  if (!enabledIndexes.length) {
    return;
  }

  const enabledPosition = enabledIndexes.indexOf(currentIndex);
  let nextIndex = currentIndex;

  if (event.key === keyHome) {
    nextIndex = enabledIndexes[0];
  } else if (event.key === keyEnd) {
    nextIndex = enabledIndexes[enabledIndexes.length - 1];
  } else if (enabledPosition >= 0) {
    const direction = event.key === keyArrowRight ? 1 : -1;
    const nextPosition = (enabledPosition + direction + enabledIndexes.length) % enabledIndexes.length;
    nextIndex = enabledIndexes[nextPosition];
  }

  const nextTab = props.tabs[nextIndex];
  if (!nextTab) {
    return;
  }

  setActiveTab(nextTab.id);
  tabButtonElements.value[nextIndex]?.focus();
}

function getTabId(tabId: string): string {
  return `${generatedId}-${tabId}-tab`;
}

function getPanelId(tabId: string): string {
  return `${generatedId}-${tabId}-panel`;
}
</script>
